// コード生成関連のハンドラー
import type { SettingItem } from '../types/index.js';
import { generateBatchCode } from '../components/code-generator.js';
import { getImportedApps, getSettings, getLastGeneratedCode, setLastGeneratedCode } from '../state/app-state.js';
import { getSettingCode } from '../utils/i18n.js';
import { t } from '../utils/i18n.js';
import { getVariableValueLocal } from './storage-handlers.js';
import { saveSelectionStateLocal } from './storage-handlers.js';
import { updateSelectedCount } from './selection-handlers.js';

/**
 * 変数を置換したコードを取得
 */
export function getItemCodeWithVariables(item: SettingItem): string {
    // 翻訳されたコードを取得
    const translatedCode = getSettingCode(item.id, item.code || '');
    if (!translatedCode) return '';
    
    if (!item.variables || item.variables.length === 0) {
        return translatedCode;
    }
    
    let code = translatedCode;
    item.variables.forEach(variable => {
        const value = getVariableValueLocal(item.id, variable.name, variable.defaultValue || '');
        const regex = new RegExp(`\\{\\{${variable.name}\\}\\}`, 'g');
        code = code.replace(regex, value);
    });
    
    return code;
}

/**
 * 特定の項目のコードプレビューを更新（左カラム）
 */
export function updateCodePreview(itemId: string): void {
    const currentSettings = getSettings();
    if (!currentSettings) return;
    
    // 該当する項目を探す
    let targetItem: SettingItem | null = null;
    currentSettings.categories.forEach(category => {
        category.sections.forEach(section => {
            section.items.forEach(item => {
                if (item.id === itemId) {
                    targetItem = item;
                }
            });
        });
    });
    
    if (!targetItem) return;
    
    // 変数を置換したコードを取得
    const codeWithVariables = getItemCodeWithVariables(targetItem);
    
    // 左カラムのコードプレビューを更新
    const codePreviewDiv = document.getElementById(`codePreview_${itemId}`);
    if (codePreviewDiv) {
        if (codeWithVariables.trim().length > 0) {
            const safeCode = codeWithVariables
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            codePreviewDiv.innerHTML = `
                <span class="text-gray-500 pr-1">bat&gt;</span>
                <pre class="inline whitespace-pre-wrap align-middle">${safeCode}</pre>
            `;
        } else {
            // codePreviewEmptyから"bat&gt; "を削除（翻訳文字列の変更に対応するため正規表現を使用）
            const emptyText = t('codePreviewEmpty').replace(/^bat&gt;\s*/, '');
            codePreviewDiv.innerHTML = `
                <span class="text-gray-500 pr-1">bat&gt;</span>
                <span class="text-gray-500">(${emptyText})</span>
            `;
        }
    }
}

/**
 * コード更新
 */
export function updateCode(withHighlight: boolean = true): void {
    // 選択状態を保存（テーブルエディタでPCが選択されている場合は、そのPCの設定も更新）
    saveSelectionStateLocal();
    
    // 少し遅延させてから警告表示を更新（DOM更新を待つ）
    // 循環参照を避けるため、動的インポートを使用
    setTimeout(async () => {
        const { updateAdminToggleState, updateRebootToggleState } = await import('./selection-handlers.js');
        updateAdminToggleState();
        updateRebootToggleState();
    }, 0);

    const codeOutput = document.getElementById('codeOutput');
    const currentSettings = getSettings();
    if (!codeOutput || !currentSettings) return;

    const oldCode = getLastGeneratedCode() || codeOutput.textContent || '';
    const newCode = generateBatchCode(getImportedApps(), currentSettings);
    setLastGeneratedCode(newCode);

    // コードを行単位で描画（ハイライト用）
    const newLines = newCode.split('\n');
    const escaped = newLines.map((line, idx) => {
        const safe = line
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        return `<div class="code-line" data-line="${idx}">${safe === '' ? '&nbsp;' : safe}</div>`;
    }).join('');
    codeOutput.innerHTML = escaped;

    // 差分ハイライト＆スクロール
    if (withHighlight && oldCode) {
        const oldLines = oldCode.split('\n');
        const minLen = Math.min(oldLines.length, newLines.length);

        // 1) 先頭から共通部分をスキップ
        let head = 0;
        while (head < minLen && oldLines[head] === newLines[head]) {
            head++;
        }

        // 変化なしなら終了
        if (head < oldLines.length || head < newLines.length) {
            // 2) 末尾から共通部分をスキップ
            let tail = 0;
            while (
                tail < minLen - head &&
                oldLines[oldLines.length - 1 - tail] === newLines[newLines.length - 1 - tail]
            ) {
                tail++;
            }

            // newCode 側で変更されている範囲 [head, newLines.length - tail)
            let start = head;
            const end = newLines.length - tail; // 非含む

            if (start < end) {
                // 実際に内容がある最初の行までスクロール（空行はスキップ）
                let scrollIndex = start;
                while (scrollIndex < end && scrollIndex < newLines.length && newLines[scrollIndex]?.trim() === '') {
                    scrollIndex++;
                }
                const firstEl = codeOutput.querySelector(`[data-line="${scrollIndex}"]`);
                if (firstEl) {
                    // スマホの場合、右ペイン全体がスクロールされないように、コード表示部分内でスクロール
                    const scrollContainer = codeOutput.closest('[class*="overflow-y-auto"]') as HTMLElement | null;
                    if (scrollContainer && window.matchMedia('(max-width: 767px)').matches) {
                        // スマホの場合：コード表示部分内でスクロール
                        const containerRect = scrollContainer.getBoundingClientRect();
                        const elementRect = firstEl.getBoundingClientRect();
                        const scrollTop = scrollContainer.scrollTop;
                        const targetScrollTop = scrollTop + (elementRect.top - containerRect.top) - (containerRect.height / 2) + (elementRect.height / 2);
                        scrollContainer.scrollTo({
                            top: targetScrollTop,
                            behavior: 'smooth'
                        });
                    } else {
                        // デスクトップの場合：通常のscrollIntoView
                        firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }

                // 変更範囲をハイライト（空行も含めてブロックとして扱う）
                for (let i = start; i < end; i++) {
                    const el = codeOutput.querySelector(`[data-line="${i}"]`);
                    if (!el) continue;
                    el.classList.add('code-line-highlight');
                    setTimeout(() => {
                        const el2 = codeOutput.querySelector(`[data-line="${i}"]`);
                        if (el2) el2.classList.remove('code-line-highlight');
                    }, 1000);
                }
            }
        }
    }

    // チェック状態を永続化
    saveSelectionStateLocal();
    updateSelectedCount();
}

